import {Component, computed, inject, input, output, TemplateRef} from "@angular/core";
import {UntilDestroy} from "@ngneat/until-destroy";
import {Translations} from "@translations";
import {AssessableItem, ChecklistItemType, ScenarioRecording} from "@models";
import {AssessableContextService} from "../services/assessment-context.service";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {SimConfigurationService, SimUtilityService, TrackByMethods, TypePredicates} from "@services";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";

@UntilDestroy()
@Component({
	selector: "sim-assessable-answer-item",
	templateUrl: "./assessable-answer-item.component.html",
	styleUrl: "./assessable-answer-item.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class AssessableAnswerItemComponent {
	// Enums
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly ChecklistItemType = ChecklistItemType;
	protected readonly T = Translations;
	protected readonly TrackByMethods = TrackByMethods;
	protected readonly SimConfigurationService = SimConfigurationService;
	protected readonly SimUtilityService = SimUtilityService;
	protected readonly TypePredicates = TypePredicates;

	// I/O
	public scenarioRecording = input.required<ScenarioRecording | null>();
	public item = input.required<AssessableItem>();
	public mode = input.required<AssessablePanelMode>();
	public expanded = input.required<boolean>();
	public indexInGroup = input.required<number>();
	public groupIndex = input.required<number | null>();
	public hasMiddleAdditionalContent = input(false);
	public compactLayout = input(false);
	public hideExpandButton = input(false);
	public isInArchive = input(false);
	public hideExpansionArea = input(false);
	public disabled = input(false);
	public isHintsDialogEnabled = input(false);

	public scoreTemplate = input<TemplateRef<void>>();
	public yesNoItemTemplate = input<TemplateRef<void>>();
	public ratingItemTemplate = input<TemplateRef<void>>();
	public freeAnswerItemTemplate = input<TemplateRef<void>>();
	public multipleChoiceItemTemplate = input<TemplateRef<void>>();
	public singleChoiceItemTemplate = input<TemplateRef<void>>();
	public middleAdditionalContentTemplate = input<TemplateRef<void>>();

	// Outputs
	public readonly onOpenHintsDialog = output<void>();
	public readonly onTimestampClicked = output<void>();
	public readonly onExpand = output<void>();
	public readonly onCollapse = output<void>();

	// Signals
	protected assessableItemId = computed(() => this.item().assessableItemId);
	protected itemControl = computed(() => this.contextService.findItemControl(this.assessableItemId()));
	protected note = computed(() => this.item().note);
	protected answerTime = computed(() => this.item().answerTime);
	protected isExpandable = computed(() => {
		// overrules the other conditions
		if (this.hideExpansionArea()) {
			return false;
		}
		return this.hasMiddleAdditionalContent();
	});
	protected type = computed(() => this.item().type);
	protected isAnswerInputInHeader = computed(() => {
		const isCompactItemType = [ChecklistItemType.YesNoOption, ChecklistItemType.ThreeStarOption].includes(this.type());
		return this.compactLayout() ? isCompactItemType : false;
	});
	protected isExpandButtonHidden = computed(() => this.hideExpandButton() || !this.isExpandable());
	protected indexString = computed(() => {
		const groupIndex = this.groupIndex()
			? Number(this.groupIndex()) + 1
			: (this.contextService.form.value.assessableGroups?.length ?? 0) + 1;
		const indexInGroup = this.indexInGroup() + 1;

		return `${groupIndex}.${indexInGroup}.`;
	});

	// Properties
	public tooltipDisabled = false;

	// Injections
	public contextService: AssessableContextService<AssessableItem> = inject(AssessableContextService<AssessableItem>);

	public togglePanel() {
		if (!this.isExpandable()) {
			return;
		}

		if (this.expanded()) {
			this.onCollapse.emit();
		} else {
			this.onExpand.emit();
		}
	}
}
